/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


class SanCarousel extends HTMLElement {
  constructor() {
      super();
      this.state = { index: 0, isDragging: false, startX: 0, dragOffset: 0 };
      this.cardWidth = 0;
      
      // Bind methods once
      this.onResize = this.debounce(() => this.init(), 150);
      this.onDragStart = this.handleDrag.bind(this, 'start');
      this.onDragMove = this.handleDrag.bind(this, 'move');
      this.onDragEnd = this.handleDrag.bind(this, 'end');
  }

  connectedCallback() {
      this.wrapper = this.querySelector('.carousel-wrapper');
      this.dotsContainer = this.querySelector('.carousel-dots');
      this.cards = this.querySelectorAll('.carousel-card');
      this.desktopCards = parseInt(this.dataset.cardsPerView) || 3;
      
      if (!this.wrapper || !this.cards.length) return;
      
      this.init();
      this.bindEvents();
  }

  disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
  }

  debounce(fn, ms) {
      let id;
      return () => { clearTimeout(id); id = setTimeout(fn, ms); };
  }

  get itemsPerView() {
      const w = window.innerWidth;
      return w <= 768 ? 1 : w <= 1024 ? (this.desktopCards === 4 ? 3 : 2) : this.desktopCards;
  }

  get maxIndex() {
      return Math.max(0, this.cards.length - this.itemsPerView);
  }

  init() {
      // Cache card width
      const gap = parseFloat(getComputedStyle(this.wrapper).gap) || 30;
      this.cardWidth = this.cards[0].offsetWidth + gap;
      
      // Clamp index
      if (this.state.index > this.maxIndex) this.state.index = this.maxIndex;
      
      // Build dots with event delegation
      const fragment = document.createDocumentFragment();
      for (let i = 0; i <= this.maxIndex; i++) {
          const dot = document.createElement('div');
          dot.className = 'dot';
          dot.dataset.index = i;
          if (i === this.state.index) dot.classList.add('active');
          fragment.appendChild(dot);
      }
      this.dotsContainer.innerHTML = '';
      this.dotsContainer.appendChild(fragment);
      
      this.slide();
  }

  bindEvents() {
      // Navigation
      this.querySelector('.prev')?.addEventListener('click', () => this.go(-1));
      this.querySelector('.next')?.addEventListener('click', () => this.go(1));
      
      // Dots delegation
      this.dotsContainer.addEventListener('click', (e) => {
          if (e.target.classList.contains('dot')) {
              this.state.index = +e.target.dataset.index;
              this.slide();
          }
      });
      
      // Touch events for mobile
      this.wrapper.addEventListener('touchstart', this.onDragStart, { passive: false });
      this.wrapper.addEventListener('touchmove', this.onDragMove, { passive: false });
      this.wrapper.addEventListener('touchend', this.onDragEnd);
      this.wrapper.addEventListener('touchcancel', this.onDragEnd);
      
      // Mouse events for desktop
      this.wrapper.addEventListener('mousedown', this.onDragStart);
      this.wrapper.addEventListener('mousemove', this.onDragMove);
      this.wrapper.addEventListener('mouseup', this.onDragEnd);
      this.wrapper.addEventListener('mouseleave', this.onDragEnd);
      
      window.addEventListener('resize', this.onResize);
  }

  handleDrag(type, e) {
      const s = this.state;
      const isTouch = e.type.startsWith('touch');
      const clientX = isTouch ? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX : e.clientX;
      
      if (type === 'start') {
          if (!isTouch) e.preventDefault();
          s.isDragging = true;
          s.startX = clientX;
          s.dragOffset = 0;
          this.wrapper.style.transition = 'none';
      } 
      else if (type === 'move' && s.isDragging) {
          s.dragOffset = clientX - s.startX;
          
          // Prevent vertical scroll only if horizontal swipe detected
          if (Math.abs(s.dragOffset) > 10) {
              e.preventDefault();
          }
          
          const tx = -s.index * this.cardWidth + s.dragOffset;
          this.wrapper.style.transform = `translateX(${tx}px)`;
      } 
      else if (type === 'end' && s.isDragging) {
          s.isDragging = false;
          this.wrapper.style.transition = '';
          
          if (Math.abs(s.dragOffset) > 50) {
              this.go(s.dragOffset < 0 ? 1 : -1);
          } else {
              this.slide();
          }
      }
  }

  go(dir) {
      const s = this.state;
      s.index = dir > 0 
          ? (s.index < this.maxIndex ? s.index + 1 : 0)
          : (s.index > 0 ? s.index - 1 : this.maxIndex);
      this.slide();
  }

  slide() {
      this.wrapper.style.transform = `translateX(${-this.state.index * this.cardWidth}px)`;
      this.dotsContainer.querySelectorAll('.dot').forEach((dot, i) => {
          dot.classList.toggle('active', i === this.state.index);
      });
  }
}
customElements.define('san-carousel', SanCarousel);
/**
* San Video Section - Performance Optimized
* Lazy loads video when section comes into viewport
* Connection-aware: loads earlier on fast connections, later on slow ones
* Safe to run on any page - won't error if section doesn't exist
*/
(function() {
'use strict';

// Check connection speed for smarter loading
function getConnectionType() {
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return 'unknown';
  
  // Save-Data mode or slow connection
  if (connection.saveData) return 'slow';
  
  var type = connection.effectiveType;
  if (type === '4g') return 'fast';
  if (type === '3g') return 'medium';
  return 'slow'; // 2g or slow-2g
}

function initSanVideoSection(sectionId) {
  var wrapper = document.getElementById('san-video-wrapper-' + sectionId);
  var template = document.getElementById('san-video-template-' + sectionId);
  var media = document.getElementById('san-video-media-' + sectionId);

  if (!wrapper || !template || !media) return;

  var isLoaded = false;
  var connectionType = getConnectionType();
  
  // Adjust rootMargin based on connection speed
  // Fast: preload 300px before visible
  // Medium: preload 100px before visible  
  // Slow: only load when actually visible (0px)
  var rootMargin = connectionType === 'fast' ? '300px 0px' : 
                   connectionType === 'medium' ? '100px 0px' : '0px 0px';

  function loadVideo() {
    if (isLoaded) return;
    isLoaded = true;

    wrapper.classList.add('san-video-loading');

    var content = template.content.cloneNode(true);
    media.appendChild(content);

    var video = media.querySelector('video');
    if (video) {
      video.preload = 'auto';
      video.addEventListener('canplay', function() {
        wrapper.classList.remove('san-video-loading');
        wrapper.classList.add('san-video-loaded');
        video.play().catch(function() {});
      }, { once: true });
      
      // Fallback if canplay doesn't fire
      video.addEventListener('loadeddata', function() {
        wrapper.classList.remove('san-video-loading');
        wrapper.classList.add('san-video-loaded');
      }, { once: true });
    } else {
      // iframe (YouTube/Vimeo)
      wrapper.classList.remove('san-video-loading');
      wrapper.classList.add('san-video-loaded');
    }
  }

  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          // On slow connections, load immediately when visible
          // On fast connections, use idle callback
          if (connectionType === 'slow') {
            loadVideo();
          } else if ('requestIdleCallback' in window) {
            requestIdleCallback(loadVideo, { timeout: 500 });
          } else {
            setTimeout(loadVideo, 50);
          }
          observer.disconnect();
        }
      });
    }, {
      rootMargin: rootMargin,
      threshold: 0
    });

    observer.observe(wrapper);
  } else {
    // Fallback for old browsers - load after page loads
    if (document.readyState === 'complete') {
      loadVideo();
    } else {
      window.addEventListener('load', loadVideo, { once: true });
    }
  }
}

function initAllSanVideoSections() {
  var sections = document.querySelectorAll('.san-video-section');
  if (!sections.length) return;
  
  sections.forEach(function(section) {
    var sectionId = section.id ? section.id.replace('san-video-', '') : null;
    if (sectionId) {
      initSanVideoSection(sectionId);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSanVideoSections);
} else {
  initAllSanVideoSections();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    var section = event.target.querySelector('.san-video-section');
    if (section) {
      var sectionId = section.id ? section.id.replace('san-video-', '') : null;
      if (sectionId) {
        initSanVideoSection(sectionId);
      }
    }
  });
}
})();

(function () {
'use strict';

const PREFIX = 'Search for ';
const PRODUCTS = ['Tawa', 'Kadai', 'Pan', 'Dutch oven'];
const DRAWER_ID = 'search-drawer';

const state = {
  idx: 0,
  char: 0,
  typing: false,
  deleting: false,
  timer: null,
  input: null,
  drawer: null,
  original: '',
  observer: null,
  initialized: false
};

const qs = (root, sel) => root && root.querySelector(sel);

function getDrawer() {
  return state.drawer || (state.drawer = document.getElementById(DRAWER_ID));
}

function getInput() {
  const drawer = getDrawer();
  return drawer ? qs(drawer, '.predictive-search__input') : null;
}

function clearTimer() {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
}

function stopAnimation() {
  state.typing = false;
  clearTimer();
  if (state.input) state.input.placeholder = state.original;
}

function canAnimate() {
  return (
    state.input &&
    state.drawer &&
    state.drawer.hasAttribute('open') &&
    !state.input.value
  );
}

function startAnimation() {
  if (state.typing || !canAnimate()) return;
  state.typing = true;
  state.idx = state.char = 0;
  state.deleting = false;
  animate();
}

function animate() {
  if (!state.typing || !canAnimate()) {
    stopAnimation();
    return;
  }

  const product = PRODUCTS[state.idx];
  state.input.placeholder =
    PREFIX + product.substring(0, state.char) + '';

  if (!state.deleting) {
    if (state.char < product.length) {
      state.char++;
      state.timer = setTimeout(animate, 100);
    } else {
      state.deleting = true;
      state.timer = setTimeout(animate, 2000);
    }
  } else {
    if (state.char > 0) {
      state.char--;
      state.timer = setTimeout(animate, 50);
    } else {
      state.idx = (state.idx + 1) % PRODUCTS.length;
      state.deleting = false;
      state.timer = setTimeout(animate, 1000);
    }
  }
}

function bindInputEvents(input, drawer) {
  if (input._typedBound) return;
  input._typedBound = true;

  input.addEventListener('keydown', e => {
    if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')
      stopAnimation();
  });

  input.addEventListener('input', e => {
    e.target.value
      ? stopAnimation()
      : drawer.hasAttribute('open') && setTimeout(startAnimation, 300);
  });

  input.addEventListener('focus', () => {
    if (!input.value && drawer.hasAttribute('open'))
      setTimeout(startAnimation, 100);
  });

  input.addEventListener('blur', () => {
    if (!input.value && drawer.hasAttribute('open'))
      setTimeout(startAnimation, 300);
  });
}

function observeDrawer(drawer) {
  if (state.observer) state.observer.disconnect();

  state.observer = new MutationObserver(muts => {
    muts.forEach(m => {
      if (m.attributeName === 'open') {
        drawer.hasAttribute('open')
          ? setTimeout(startAnimation, 500)
          : stopAnimation();
      }
    });
  });

  state.observer.observe(drawer, {
    attributes: true,
    attributeFilter: ['open']
  });
}

function init() {
  const drawer = getDrawer();
  if (!drawer) return;

  const input = getInput();
  if (!input) return;

  if (state.input === input && state.initialized) return;

  state.drawer = drawer;
  state.input = input;
  state.original = input.placeholder || '';
  state.initialized = true;

  bindInputEvents(input, drawer);
  observeDrawer(drawer);
}

function tryInit() {
  [0, 300, 800, 1500].forEach(t => setTimeout(init, t));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tryInit);
} else {
  tryInit();
}

new MutationObserver(init).observe(document.body, {
  childList: true,
  subtree: true
});

document.addEventListener(
  'click',
  e => {
    if (e.target.closest('[aria-controls="' + DRAWER_ID + '"]')) {
      setTimeout(startAnimation, 800);
    }
  },
  true
);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () =>
    setTimeout(init, 300)
  );
}
})();
class TextSlide extends HTMLElement {
#currentIndex = 1;
#timer = null;
#observer = null;
#isTransitioning = false;
#itemCount = 0;

connectedCallback() {
  const children = Array.from(this.children);
  if (children.length <= 1) return;
  this.#itemCount = children.length;
  this.interval = parseInt(this.getAttribute('interval')) || 2000;
  this.wrapper = document.createElement('div');
  this.wrapper.classList.add('wrapper');
  const items = children.map(child => {
    const div = document.createElement('div');
    div.classList.add('item');
    div.appendChild(child);
    return div;
  });

  this.wrapper.appendChild(items.at(-1).cloneNode(true));
  items.forEach(item => this.wrapper.appendChild(item));
  this.wrapper.appendChild(items[0].cloneNode(true));

  this.appendChild(this.wrapper);

  this.#setPosition(false);
  this.#attachTransitionEnd();
  this.#attachVisibilityPause();
  this.#attachIntersectionPause();
}
#setPosition(animate = true) {
  this.wrapper.style.transition = animate ? 'transform 0.5s ease' : 'none';
  this.wrapper.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
}
#jumpTo(index) {
  this.#currentIndex = index;
  this.wrapper.style.transition = 'none';
  this.wrapper.style.transform = `translateX(-${index * 100}%)`;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    this.#isTransitioning = false;
  }));
}

#startSlide() {
  this.#timer = setInterval(() => {
    if (this.#isTransitioning) return;
    this.#isTransitioning = true;
    this.#currentIndex++;
    this.#setPosition(true);
  }, this.interval);
}

#stopSlide() {
  clearInterval(this.#timer);
  this.#timer = null;
}

#attachTransitionEnd() {
  const totalItems = this.#itemCount + 2;

  this.wrapper.addEventListener('transitionend', () => {
    if (this.#currentIndex === totalItems - 1) {
      this.#jumpTo(1);
    } else if (this.#currentIndex === 0) {
      this.#jumpTo(this.#itemCount);
    } else {
      this.#isTransitioning = false;
    }
  });
}
#attachVisibilityPause() {
  this._onVisibility = () => {
    if (document.hidden) {
      this.#stopSlide();
    } else {
      this.#startSlide();
    }
  };
  document.addEventListener('visibilitychange', this._onVisibility);
  this.#startSlide();
}
#attachIntersectionPause() {
  this.#observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      if (!this.#timer) this.#startSlide();
    } else {
      this.#stopSlide();
    }
  }, { threshold: 0 });

  this.#observer.observe(this);
}
disconnectedCallback() {
  this.#stopSlide();
  this.#observer?.disconnect();
  document.removeEventListener('visibilitychange', this._onVisibility);
}
}
customElements.define('text-slide', TextSlide);


(function () {
'use strict';

/* ── CONFIG ──────────────────────────────────────────────────────── */
const CFG = {
  DATA_URL:        'https://cdn.shopify.com/s/files/1/0717/2653/0775/files/updated_pincodes_format.json?v=1779198835',
  UNAVAIL_MSG:     "Sorry, we do not ship to this pincode. Please try another one.",
  DISPATCH_CUTOFF: 15,
};

const DAYS_RANGE_MAP = { '2': [2, 3], '3': [3, 4], '4': [4, 6], '5': [5, 7] };

function getDeliveryDates(days) {
  const daysStr = String(days).trim();

  const now    = new Date();
  const cutoff = new Date();
  cutoff.setHours(CFG.DISPATCH_CUTOFF, 0, 0, 0);

  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const fmt = d => d.getDate() + ' ' + d.toLocaleString('en-IN', { month: 'long' });
  // "Sunday, 28 June" — weekday + earliest delivery date
  const fmtFull = d => WEEKDAYS[d.getDay()] + ', ' + fmt(d);

  // Special case: 1-day delivery → "Tomorrow" if before cutoff, else 2 days out
  if (daysStr === '1') {
    if (now < cutoff) {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { range: 'Tomorrow', first: fmtFull(t) };
    }
    const target = new Date(now);
    target.setDate(target.getDate() + 2);
    return { range: fmt(target), first: fmtFull(target) };
  }

  const range = DAYS_RANGE_MAP[daysStr];
  if (!range) return { range: String(days), first: String(days) };

  // If past cutoff, dispatch starts tomorrow — shift base by 1
  const base = new Date(now);
  if (now >= cutoff) base.setDate(base.getDate() + 1);

  const start = new Date(base);
  start.setDate(start.getDate() + range[0]);

  const end = new Date(base);
  end.setDate(end.getDate() + range[1]);

  return { range: fmt(start) + ' – ' + fmt(end), first: fmtFull(start) };
}

/* ── Shared data state (one fetch, shared across all instances) ───── */
let pincodeData   = null;
let loadState     = 'idle';
let loadCallbacks = [];

function loadData(cb) {
  if (loadState === 'done' || loadState === 'error') { cb?.(); return; }
  if (cb) loadCallbacks.push(cb);
  if (loadState === 'loading') return;

  loadState = 'loading';
  fetch(CFG.DATA_URL)
    .then(r => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .then(data => { pincodeData = data; loadState = 'done'; })
    .catch(err => {
      console.warn('[sandeep-edd] Failed to load pincode data', err);
      pincodeData = null;
      loadState   = 'error';
    })
    .finally(() => {
      const cbs = loadCallbacks.splice(0);
      cbs.forEach(fn => fn());
    });
}

function resetLoadState() {
  if (loadState === 'error') {
    pincodeData = null;
    loadState   = 'idle';
  }
}

class SandeepEdd extends HTMLElement {

  connectedCallback() {
    this._timer = null;
    this._cacheRefs();
    this._bindEvents();
    this._setState('default');
    this._schedulePreload();
  }

  disconnectedCallback() {
    clearInterval(this._timer);
  }

  _cacheRefs() {
    const q = s => this.querySelector(s);
    this._widget     = q('.pc-widget');
    this._input      = q('.pc-input');
    this._btn        = q('.pc-btn');
    this._dateEl       = q('[data-pc-date]');
    this._dateEls      = this.querySelectorAll('[data-pc-date]');
    this._dateFirstEls = this.querySelectorAll('[data-pc-date-first]');
    this._dispatchEl   = q('[data-pc-dispatch]');
  }

  _setState(state) {
    if (!this._widget) return;
    this._widget.dataset.state = state;

    switch (state) {
      case 'default':
        this._input.disabled = false;
        this._btn.disabled   = true;
        break;
      case 'filled':
        this._input.disabled = false;
        this._btn.disabled   = false;
        break;
      case 'loading':
        this._input.disabled = true;
        this._btn.disabled   = true;
        break;
      case 'express':
      case 'standard':
      case 'unavail':
      case 'error':
        this._input.disabled = true;
        this._btn.disabled   = false;
        break;
    }
  }

  _bindEvents() {
    this._input.addEventListener('input', () => {
      const val = this._input.value.replace(/\D/g, '').slice(0, 6);
      if (this._input.value !== val) this._input.value = val;
      this._setState(val.length === 6 ? 'filled' : 'default');
    });

    this._input.addEventListener('keydown', e => {
      if (e.key === 'Enter' && this._input.value.length === 6 && !this._input.disabled) {
        e.preventDefault();
        this._doCheck();
      }
    });

    this._btn.addEventListener('click', () => {
      const state = this._widget.dataset.state;
      if (state === 'filled') {
        this._doCheck();
      } else if (state === 'express' || state === 'standard' || state === 'unavail' || state === 'error') {
        this._reset();
      }
    });

    this.addEventListener('click', e => {
      const retry = e.target.closest('[data-pc-action="retry"]');
      if (!retry) return;
      e.preventDefault();
      e.stopPropagation();
      resetLoadState();
      this._doCheck();
    });
  }

  _schedulePreload() {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this._requestIdleOrLoad();
      }, { rootMargin: '200px' });
      io.observe(this);
    } else {
      setTimeout(() => this._requestIdleOrLoad(), 1500);
    }
  }

  _requestIdleOrLoad() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loadData(), { timeout: 2000 });
    } else {
      setTimeout(() => loadData(), 200);
    }
  }

  _doCheck() {
    const pin = this._input.value.trim();
    if (pin.length !== 6) return;

    this._setState('loading');
    clearInterval(this._timer);

    loadData(() => this._showResult(pin));
  }

  _showResult(pin) {
    if (loadState === 'error') {
      this._setState('error');
      return;
    }

    let info = null;
    const cacheKey = 'pc_' + pin;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) info = JSON.parse(cached);
    } catch (_) {}

    if (!info && pincodeData) {
      info = pincodeData[pin] || null;
      try { sessionStorage.setItem(cacheKey, JSON.stringify(info)); } catch (_) {}
    }

    const available = info && info.available !== false;

    if (!available) {
      this._setState('unavail');
      return;
    }

    const days   = String(info.days || '').trim();
    const dates  = getDeliveryDates(info.days);

    this._dateEls.forEach(el => { el.textContent = dates.range; });
    this._dateFirstEls.forEach(el => { el.textContent = dates.first; });

    if (days === '1') {
      this._setState('express');
      this._startCountdown();
    } else {
      this._setState('standard');
    }
  }

  _startCountdown() {
    clearInterval(this._timer);
    if (!this._dispatchEl) return;

    const el = this._dispatchEl;

    const tick = () => {
      if (!el.isConnected) { clearInterval(this._timer); return; }

      const now    = new Date();
      const cutoff = new Date();
      cutoff.setHours(CFG.DISPATCH_CUTOFF, 0, 0, 0);

      if (now >= cutoff) {
        el.textContent = '';
        clearInterval(this._timer);
        return;
      }

      const diff = cutoff - now;
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      const hPart = h > 0 ? h + 'h ' : '';

      el.innerHTML = 'Order within <strong>'
        + hPart
        + String(m).padStart(2, '0') + 'm '
        + String(s).padStart(2, '0') + 's'
        + '</strong>';
    };

    tick();
    this._timer = setInterval(tick, 1000);
  }

  _reset() {
    clearInterval(this._timer);
    this._timer = null;
    this._input.value = '';
    if (this._dispatchEl) this._dispatchEl.textContent = '';
    this._setState('default');
    this._input.focus();
  }
}

customElements.define('sandeep-edd', SandeepEdd);

})();

/**
* Shared AJAX add-to-cart helper.
* Adds a variant to the cart and fires the theme's cart events (variant:added,
* cart:updated, cart:refresh, cart-notification:show) so the mini-cart/drawer,
* cart count and notifications update. Reused by every custom element that needs
* to add to cart, instead of duplicating the fetch + event plumbing.
*
* @param {string|number} variantId
* @param {{ quantity?: number, dispatcher?: EventTarget }} [options]
* @returns {Promise<{ ok: boolean, redirected?: boolean, responseJson: any }>}
*/
async function sanAddToCart(variantId, { quantity = 1, dispatcher } = {}) {
const source = dispatcher || document.documentElement;

const addResponse = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  body: JSON.stringify({
    id: parseInt(variantId, 10),
    quantity,
    sections: ['mini-cart']
  })
});

const responseJson = await addResponse.json();

if (addResponse.ok) {
  if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
    window.location.href = `${Shopify.routes.root}cart`;
    return { ok: true, redirected: true, responseJson };
  }

  source.dispatchEvent(new CustomEvent('variant:added', {
    bubbles: true,
    detail: {
      variant: responseJson.hasOwnProperty('items') ? responseJson['items'][0] : responseJson
    }
  }));

  const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
  const cartContent = await cartResponse.json();

  document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
    bubbles: true,
    detail: { cart: cartContent }
  }));

  cartContent['sections'] = responseJson['sections'];

  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
    bubbles: true,
    detail: {
      cart: cartContent,
      openMiniCart: window.themeVariables.settings.cartType === 'drawer'
    }
  }));
} else {
  source.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

source.dispatchEvent(new CustomEvent('cart-notification:show', {
  bubbles: true,
  cancelable: true,
  detail: {
    status: addResponse.ok ? 'success' : 'error',
    error: responseJson['description'] || ''
  }
}));

return { ok: addResponse.ok, responseJson };
}

class ProductOptionSelector extends HTMLElement {
connectedCallback() {
  this._isAdding = false;
  this._activeIndex = -1;

  this._cards = Array.from(this.querySelectorAll('.pos__card'));
  this._headingValue = this.querySelector('[data-pos-heading-value]');

  this._variantsMap = this._cards.map(card => {
    const script = card.querySelector('script[data-pos-variants]');
    return script ? JSON.parse(script.textContent) : [];
  });

  const optScript = this.querySelector('script[data-pos-options]');
  this._optionsMap = optScript ? JSON.parse(optScript.textContent) : [];

  this._selectedOptions = [];
  this._cards.forEach((card, i) => {
    const opts = {};
    card.querySelectorAll('.pos__swatch--active').forEach(sw => {
      opts[sw.dataset.optionName] = sw.dataset.optionValue;
    });
    this._selectedOptions[i] = opts;
  });

  this.addEventListener('click', this._onClick.bind(this));
}

_onClick(e) {
  const atcBtn = e.target.closest('[data-pos-atc]');
  if (atcBtn) { this._addToCart(); return; }

  const swatch = e.target.closest('.pos__swatch');
  if (swatch) { this._onSwatchClick(swatch); return; }

  const card = e.target.closest('.pos__card');
  if (card) {
    const index = parseInt(card.dataset.posIndex);
    if (!isNaN(index)) this._selectCard(index);
  }
}

_selectCard(index) {
  if (index === this._activeIndex) {
    this._activeIndex = -1;
    this._cards[index].classList.remove('pos__card--active');
    if (this._headingValue) this._headingValue.textContent = '';
    return;
  }

  this._activeIndex = index;

  this._cards.forEach((card, i) => {
    card.classList.toggle('pos__card--active', i === index);
  });

  if (this._headingValue) {
    const title = this._cards[index].querySelector('.pos__card-title');
    if (title) this._headingValue.textContent = title.textContent;
  }
}

_onSwatchClick(swatch) {
  const card = swatch.closest('.pos__card');
  const index = parseInt(card.dataset.posIndex);
  const optName = swatch.dataset.optionName;
  const optValue = swatch.dataset.optionValue;

  this._selectedOptions[index][optName] = optValue;

  const group = swatch.closest('.pos__option-group');
  group.querySelectorAll('.pos__swatch').forEach(sw => {
    sw.classList.toggle('pos__swatch--active', sw.dataset.optionValue === optValue);
  });

  const label = group.querySelector('[data-pos-option-value]');
  if (label) label.textContent = optValue;

  this._updateVariant(index);
}

_getVariant(index) {
  const variants = this._variantsMap[index];
  const options = this._optionsMap[index];
  const selected = this._selectedOptions[index];
  if (!variants || !options) return null;

  return variants.find(v =>
    options.every((optName, i) => v.options[i] === selected[optName])
  ) || null;
}

_formatMoney(cents) {
  if (typeof formatMoney === 'function') return formatMoney(cents);
  return (cents / 100).toLocaleString('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 0
  });
}

_updateVariant(index) {
  const variant = this._getVariant(index);
  const card = this._cards[index];
  if (!variant || !card) return;

  const img = card.querySelector('[data-pos-image]');
  if (img && variant.featured_image) {
    img.src = variant.featured_image;
  }

  const priceEl = card.querySelector('[data-pos-price]');
  if (priceEl) priceEl.innerHTML = this._formatMoney(variant.price);

  const compareEl = card.querySelector('[data-pos-compare-price]');
  if (compareEl) {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.innerHTML = this._formatMoney(variant.compare_at_price);
      compareEl.style.display = '';
    } else {
      compareEl.style.display = 'none';
    }
  }

  const atcBtn = card.querySelector('[data-pos-atc]');
  if (atcBtn) {
    atcBtn.dataset.variantId = variant.id;
    atcBtn.disabled = !variant.available;
    atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
  }
}

async _addToCart() {
  if (this._isAdding) return;

  const card = this._cards[this._activeIndex];
  const atcBtn = card?.querySelector('[data-pos-atc]');
  const variantId = atcBtn?.dataset.variantId;
  if (!variantId) return;

  this._isAdding = true;

  if (atcBtn) {
    atcBtn.disabled = true;
    atcBtn.setAttribute('aria-busy', 'true');
    atcBtn.textContent = 'Adding...';
  }

  try {
    const { ok, redirected } = await sanAddToCart(variantId, { dispatcher: this });
    if (redirected) return;

    if (atcBtn) {
      atcBtn.removeAttribute('aria-busy');
      if (ok) {
        atcBtn.textContent = 'Added!';
        setTimeout(() => {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
        }, 1500);
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }
    }
  } catch (err) {
    console.error('[ProductOptionSelector]', err);
    if (atcBtn) {
      atcBtn.removeAttribute('aria-busy');
      atcBtn.disabled = false;
      atcBtn.textContent = 'Add to Cart';
    }
  } finally {
    this._isAdding = false;
  }
}
}

customElements.define('product-option-selector', ProductOptionSelector);

class SanPhilosophy extends HTMLElement {
constructor() {
  super();
  this.index = 0;
  this.timer = null;
  this.isVisible = false;
  this.onPointClick = this.handlePointClick.bind(this);
  this.onKeydown = this.handleKeydown.bind(this);
}

connectedCallback() {
  this.points = Array.from(this.querySelectorAll('.san-philosophy__point'));
  this.images = Array.from(this.querySelectorAll('.san-philosophy__image'));
  this.panels = Array.from(this.querySelectorAll('.san-philosophy__panel'));
  this.nav = this.querySelector('.san-philosophy__nav');
  this.total = this.points.length;

  if (!this.total) return;

  this.interval = parseInt(this.dataset.interval, 10) || 4500;
  this.index = this.points.findIndex((point) => point.classList.contains('is-active'));
  if (this.index < 0) this.index = 0;

  this.nav?.addEventListener('click', this.onPointClick);
  this.nav?.addEventListener('keydown', this.onKeydown);
  this.setupObserver();
}

disconnectedCallback() {
  this.stopAutoplay();
  this.observer?.disconnect();
  this.nav?.removeEventListener('click', this.onPointClick);
  this.nav?.removeEventListener('keydown', this.onKeydown);
}

setupObserver() {
  if (this.observer) this.observer.disconnect();

  this.observer = new IntersectionObserver(
    ([entry]) => {
      this.isVisible = entry.isIntersecting;
      if (this.isVisible) this.startAutoplay();
      else this.stopAutoplay();
    },
    { threshold: 0.2 }
  );

  this.observer.observe(this);
}

handlePointClick(event) {
  const point = event.target.closest('.san-philosophy__point');
  if (!point || !this.nav?.contains(point)) return;

  const index = parseInt(point.dataset.index, 10);
  if (Number.isNaN(index)) return;

  this.goTo(index);
  this.resetAutoplay();
}

handleKeydown(event) {
  const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
  if (!keys.includes(event.key)) return;

  event.preventDefault();

  let next = this.index;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    next = (this.index + 1) % this.total;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    next = (this.index - 1 + this.total) % this.total;
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = this.total - 1;
  }

  this.goTo(next);
  this.points[next]?.focus();
  this.resetAutoplay();
}

goTo(index) {
  if (index === this.index || index < 0 || index >= this.total) return;

  const previous = this.index;
  this.index = index;
  this.updatePoints();
  this.updateImages(previous);
  this.updatePanels();
}

updatePoints() {
  this.points.forEach((point, i) => {
    const active = i === this.index;
    point.classList.toggle('is-active', active);
    point.setAttribute('aria-selected', active ? 'true' : 'false');
    point.tabIndex = active ? 0 : -1;
  });
}

updateImages(previousIndex) {
  const previous = this.images[previousIndex];
  const next = this.images[this.index];
  if (!next) return;

  if (previous && previous !== next) {
    previous.classList.add('is-leaving');
    previous.classList.remove('is-active');

    const cleanup = (event) => {
      if (event.propertyName && event.propertyName !== 'opacity') return;
      previous.classList.remove('is-leaving');
      previous.removeEventListener('transitionend', cleanup);
    };
    previous.addEventListener('transitionend', cleanup);
  }

  requestAnimationFrame(() => {
    next.classList.add('is-active');
    next.classList.remove('is-leaving');
  });
}

updatePanels() {
  this.panels.forEach((panel, i) => {
    const active = i === this.index;
    panel.classList.toggle('is-active', active);

    if (active) {
      panel.removeAttribute('hidden');
      panel.querySelectorAll('.san-philosophy__title, .san-philosophy__description').forEach((el) => {
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.animation = '';
      });
    } else {
      panel.setAttribute('hidden', '');
    }
  });
}

startAutoplay() {
  this.stopAutoplay();
  if (this.total < 2 || !this.isVisible) return;

  this.timer = window.setInterval(() => {
    this.goTo((this.index + 1) % this.total);
  }, this.interval);
}

stopAutoplay() {
  if (this.timer) {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

resetAutoplay() {
  if (this.isVisible) this.startAutoplay();
}
}

if (!customElements.get('san-philosophy')) {
customElements.define('san-philosophy', SanPhilosophy);
}

class MaterialPointer extends HTMLElement {
constructor() {
  super();
  this.onClick = this.handleClick.bind(this);
}

connectedCallback() {
  this.tabs = Array.from(this.querySelectorAll('[data-mp-tab]'));
  this.panels = Array.from(this.querySelectorAll('[data-mp-panel]'));

  if (!this.tabs.length || !this.panels.length) return;

  this.addEventListener('click', this.onClick);

  const firstActive = this.querySelector('.mp__panel.is-active') || this.panels[0];
  if (firstActive) this.playVideos(firstActive);
}

disconnectedCallback() {
  this.removeEventListener('click', this.onClick);
}

handleClick(event) {
  const tab = event.target.closest('[data-mp-tab]');
  if (tab && this.contains(tab)) {
    this.activateTab(tab.getAttribute('data-mp-tab'));
    return;
  }

  const hotspot = event.target.closest('[data-mp-hotspot]');
  if (!hotspot || !this.contains(hotspot)) return;

  const pointerRoot = hotspot.closest('[data-mp-pointer-root]');
  if (!pointerRoot || !this.contains(pointerRoot)) return;

  const content = pointerRoot.querySelector('[data-mp-layer-content]');
  if (!content) return;

  const number = hotspot.getAttribute('data-number') || '';
  const title = hotspot.getAttribute('data-title') || '';
  const desc = hotspot.getAttribute('data-desc') || '';

  content.innerHTML =
    '<div class="mp-pointer__detail-badge">' +
    number +
    '</div><h4 class="mp-pointer__detail-title">' +
    title +
    '</h4><p class="mp-pointer__detail-desc">' +
    desc +
    '</p>';

  pointerRoot.querySelectorAll('[data-mp-hotspot]').forEach((el) => {
    el.classList.remove('is-active');
  });
  hotspot.classList.add('is-active');
}

activateTab(tabId) {
  this.tabs.forEach((tab) => {
    const active = tab.getAttribute('data-mp-tab') === tabId;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
  });

  this.panels.forEach((panel) => {
    const active = panel.getAttribute('data-mp-panel') === tabId;
    panel.classList.toggle('is-active', active);
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');

    if (active) {
      this.playVideos(panel);
    } else {
      this.pauseVideos(panel);
    }
  });
}

pauseVideos(panel) {
  panel.querySelectorAll('video').forEach((video) => {
    video.pause();
  });
}

playVideos(panel) {
  panel.querySelectorAll('video').forEach((video) => {
    video.muted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  });
}
}

if (!customElements.get('material-pointer')) {
customElements.define('material-pointer', MaterialPointer);
}

class ImageTextCards extends HTMLElement {
constructor() {
  super();
  this.onScroll = this.updateButtons.bind(this);
  this.onPrev = () => this.scrollByPage(-1);
  this.onNext = () => this.scrollByPage(1);
}

connectedCallback() {
  this.scroller = this.querySelector('[data-itc-scroller]');
  if (!this.scroller) return;

  const scope = this.closest('.itc') || this;
  this.prev = scope.querySelector('[data-itc-prev]');
  this.next = scope.querySelector('[data-itc-next]');

  if (this.prev) this.prev.addEventListener('click', this.onPrev);
  if (this.next) this.next.addEventListener('click', this.onNext);
  this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

  this.updateButtons();
}

disconnectedCallback() {
  if (this.prev) this.prev.removeEventListener('click', this.onPrev);
  if (this.next) this.next.removeEventListener('click', this.onNext);
  if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
}

scrollByPage(direction) {
  if (!this.scroller) return;
  const amount = Math.max(this.scroller.clientWidth * 0.9, 240);
  this.scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
}

updateButtons() {
  if (!this.scroller || !this.prev || !this.next) return;
  const max = this.scroller.scrollWidth - this.scroller.clientWidth;
  this.prev.disabled = this.scroller.scrollLeft <= 1;
  this.next.disabled = this.scroller.scrollLeft >= max - 1;
}
}

if (!customElements.get('image-text-cards')) {
customElements.define('image-text-cards', ImageTextCards);
}

class SanShopByRegion extends HTMLElement {
connectedCallback() {
  this._tablist = this.querySelector('[data-san-tablist]');
  this._panelsRoot = this.querySelector('[data-san-panels]');
  if (!this._tablist || !this._panelsRoot) return;

  this._tabs = Array.from(this._tablist.querySelectorAll('[data-tab-target]'));
  this._panels = Array.from(this._panelsRoot.querySelectorAll('[data-tab-panel]'));
  if (!this._tabs.length || !this._panels.length) return;

  this._onClick = this._onClick.bind(this);
  this._onKeydown = this._onKeydown.bind(this);

  this._tabs.forEach((tab) => {
    tab.addEventListener('click', this._onClick);
    tab.addEventListener('keydown', this._onKeydown);
  });
}

disconnectedCallback() {
  if (!this._tabs) return;
  this._tabs.forEach((tab) => {
    tab.removeEventListener('click', this._onClick);
    tab.removeEventListener('keydown', this._onKeydown);
  });
}

_onClick(event) {
  const tab = event.currentTarget;
  this.activate(tab.getAttribute('data-tab-target'), false);
}

_onKeydown(event) {
  const currentIndex = this._tabs.indexOf(event.currentTarget);
  if (currentIndex === -1) return;

  let nextIndex = null;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      nextIndex = (currentIndex + 1) % this._tabs.length;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      nextIndex = (currentIndex - 1 + this._tabs.length) % this._tabs.length;
      break;
    case 'Home':
      nextIndex = 0;
      break;
    case 'End':
      nextIndex = this._tabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  this.activate(this._tabs[nextIndex].getAttribute('data-tab-target'), true);
}

activate(target, focusTab) {
  this._tabs.forEach((tab) => {
    const isActive = tab.getAttribute('data-tab-target') === target;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
    if (isActive && focusTab) tab.focus();
  });

  this._panels.forEach((panel) => {
    const isActive = panel.getAttribute('data-tab-panel') === target;
    panel.classList.toggle('is-active', isActive);
    if (isActive) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  });
}
}

customElements.define('san-shop-by-region', SanShopByRegion);

class SanFlipCards extends HTMLElement {
connectedCallback() {
  this._isAdding = false;

  this._onClick = this._onClick.bind(this);
  this._onKeydown = this._onKeydown.bind(this);

  this.addEventListener('click', this._onClick);
  this.addEventListener('keydown', this._onKeydown);
}

disconnectedCallback() {
  this.removeEventListener('click', this._onClick);
  this.removeEventListener('keydown', this._onKeydown);
}

_onKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const btn = event.target.closest('[data-flip-atc]');
  if (!btn || !this.contains(btn)) return;
  event.preventDefault();
  this._handleAtc(btn);
}

_onClick(event) {
  // Clicking the add-to-cart button adds the product (never flips the card).
  const btn = event.target.closest('[data-flip-atc]');
  if (btn) {
    this._handleAtc(btn);
    return;
  }

  // Clicking the image flips the card (works on both front and back).
  const card = event.target.closest('.san-flip-card');
  if (!card) return;

  if (!card.classList.contains('is-flipped')) {
    this.querySelectorAll('.san-flip-card.is-flipped').forEach((el) => {
      if (el !== card) el.classList.remove('is-flipped');
    });
  }
  card.classList.toggle('is-flipped');
}

_handleAtc(btn) {
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  this._addToCart(btn, variantId);
}

async _addToCart(btn, variantId) {
  if (this._isAdding) return;
  this._isAdding = true;

  const originalText = btn.textContent;
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;
  btn.textContent = 'Adding...';

  try {
    const { ok, redirected } = await sanAddToCart(variantId, { dispatcher: this });
    if (redirected) return;

    btn.textContent = ok ? 'Added!' : originalText;
    if (ok) {
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    } else {
      btn.disabled = false;
    }
  } catch (err) {
    console.error('[SanFlipCards]', err);
    btn.textContent = originalText;
    btn.disabled = false;
  } finally {
    btn.removeAttribute('aria-busy');
    this._isAdding = false;
  }
}
}

if (!customElements.get('san-flip-cards')) {
customElements.define('san-flip-cards', SanFlipCards);
}

/**
 * ---------------------------------------------------------------------------
 * <order-customizer>
 * ---------------------------------------------------------------------------
 *
 * Side drawer that slides over the mini cart and lets the shopper pick gift wrap
 * and add-on product variants (one card per color). Selections are kept locally
 * and are only written to the cart when "Save and continue" is pressed.
 *
 * The gift wrap line is still owned by the theme's <gift-wrap> element, so its
 * "quantity follows the cart item count" behaviour is reused as-is.
 */
{
  const OrderCustomizerBase = customElements.get('openable-element') || HTMLElement;

  class OrderCustomizer extends OrderCustomizerBase {
    connectedCallback() {
      super.connectedCallback?.();

      this.giftWrapElement = this.querySelector('[data-customizer-gift-wrap]');
      this.giftWrapCheckbox = this.querySelector('.gift-wrap__checkbox');
      this.giftWrapToggle = this.querySelector('[data-customizer-gift-wrap-toggle]');
      this.productElements = Array.from(this.querySelectorAll('[data-customizer-product]'));
      this.saveButton = this.querySelector('[data-customizer-save]');

      this.personalizeToggle = this.querySelector('[data-customizer-personalize-toggle]');
      this.personalizeForm = this.querySelector('[data-customizer-personalize-form]');
      this.personalizeFields = {
        note: this.querySelector('[data-customizer-field="note"]'),
        recipient_name: this.querySelector('[data-customizer-field="recipient_name"]'),
        recipient_phone: this.querySelector('[data-customizer-field="recipient_phone"]'),
        sender_name: this.querySelector('[data-customizer-field="sender_name"]')
      };
      this.charRemaining = this.querySelector('[data-customizer-char-remaining]');

      this.giftWrapHandle = this.giftWrapCheckbox ? this.giftWrapCheckbox.dataset.giftWrapProduct : null;
      this.pendingGiftWrap = Boolean(this.giftWrapCheckbox && this.giftWrapCheckbox.checked);

      this.pendingQuantities = {};
      this.productElements.forEach((card) => {
        const variantId = card.dataset.variantId;
        if (!variantId) return;
        this.pendingQuantities[variantId] = parseInt(card.dataset.cartQuantity || '0', 10) || 0;
      });

      this.isSaving = false;

      this._onClick = this._onClick.bind(this);
      this._onCartDrawerClose = this._onCartDrawerClose.bind(this);
      this._onPersonalizeToggle = this._onPersonalizeToggle.bind(this);
      this._onNoteInput = this._onNoteInput.bind(this);
      this._onCartUpdated = this._onCartUpdated.bind(this);

      this.addEventListener('click', this._onClick);

      this.personalizeToggle?.addEventListener('change', this._onPersonalizeToggle);
      this.personalizeFields.note?.addEventListener('input', this._onNoteInput);
      this._onPersonalizeFieldInput = this._onPersonalizeFieldInput.bind(this);
      Object.values(this.personalizeFields).forEach((field) => {
        field?.addEventListener('input', this._onPersonalizeFieldInput);
      });
      this._syncPersonalizeRequired(Boolean(this.personalizeToggle?.checked));
      document.documentElement.addEventListener('cart:updated', this._onCartUpdated);

      this.cartDrawer = this.closest('cart-drawer');
      this.cartDrawer?.addEventListener('openable-element:close', this._onCartDrawerClose);

      // Prefill the personalization fields from the live cart. We read values in
      // JS (cart.attributes["Recipient's Name"], etc.) instead of relying on the
      // Liquid render, because attribute keys containing apostrophes don't always
      // round-trip through Liquid on re-render and the fields would come back empty.
      this._hydratePersonalization();
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.removeEventListener('click', this._onClick);
      this.personalizeToggle?.removeEventListener('change', this._onPersonalizeToggle);
      this.personalizeFields?.note?.removeEventListener('input', this._onNoteInput);
      Object.values(this.personalizeFields || {}).forEach((field) => {
        field?.removeEventListener('input', this._onPersonalizeFieldInput);
      });
      document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
      this.cartDrawer?.removeEventListener('openable-element:close', this._onCartDrawerClose);
    }

    _onCartUpdated(event) {
      const cart = event?.detail?.cart;
      if (!cart || cart.item_count > 0) return;
      this._clearPersonalizationForm();
    }

    _clearPersonalizationForm() {
      if (!this.personalizeToggle && !this.personalizeFields) return;

      Object.values(this.personalizeFields || {}).forEach((field) => {
        if (field) field.value = '';
      });

      if (this.personalizeToggle) {
        this.personalizeToggle.checked = false;
      }

      if (this.personalizeForm) {
        this.personalizeForm.hidden = true;
      }

      this._syncPersonalizeRequired(false);
      this._onNoteInput();
    }

    _onPersonalizeToggle(event) {
      if (!this.personalizeForm) return;

      const isChecked = this.personalizeToggle.checked;
      this.personalizeForm.hidden = !isChecked;
      this._syncPersonalizeRequired(isChecked);

      // Auto-scroll only on user check, not when hydrating saved cart data
      if (isChecked && event?.isTrusted) {
        requestAnimationFrame(() => {
          this.personalizeForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }

    _syncPersonalizeRequired(isRequired) {
      Object.values(this.personalizeFields || {}).forEach((field) => {
        if (!field) return;
        field.required = Boolean(isRequired);
        if (!isRequired) {
          field.setCustomValidity('');
          field.classList.remove('is-invalid');
        }
      });
    }

    /**
     * When personalization is enabled, every field must be filled before save.
     * Returns true when valid (or personalize is off); focuses the first empty field otherwise.
     */
    _validatePersonalization() {
      if (!this.personalizeToggle?.checked) return true;

      const fields = Object.values(this.personalizeFields || {}).filter(Boolean);
      let firstInvalid = null;

      fields.forEach((field) => {
        const empty = !field.value.trim();
        field.classList.toggle('is-invalid', empty);
        field.setCustomValidity(empty ? 'This field is required' : '');
        if (empty && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.reportValidity();
        return false;
      }

      return true;
    }

    _onNoteInput() {
      if (!this.charRemaining || !this.personalizeFields.note) return;
      const remaining = 500 - this.personalizeFields.note.value.length;
      this.charRemaining.textContent = String(Math.max(remaining, 0));
    }

    _onPersonalizeFieldInput(event) {
      const field = event?.target;
      if (!field) return;
      if (field.value.trim()) {
        field.setCustomValidity('');
        field.classList.remove('is-invalid');
      }
    }

    /**
     * Populates the personalization fields (gift message, recipient name/phone,
     * sender name) from the current cart so re-opening the customizer always shows
     * the previously saved values. Only fills empty fields so it never clobbers
     * something the shopper is actively typing.
     */
    async _hydratePersonalization() {
      if (!this.personalizeToggle) return;

      let cart;
      try {
        cart = await this._fetchCart();
      } catch (error) {
        return;
      }

      const attrs = cart.attributes || {};
      const values = {
        note: cart.note || attrs['Gift Message'] || '',
        recipient_name: attrs["Recipient's Name"] || '',
        recipient_phone: attrs["Recipient's Phone Number"] || '',
        sender_name: attrs["Sender's Name"] || ''
      };

      let hasData = false;

      Object.keys(values).forEach((key) => {
        const field = this.personalizeFields[key];
        if (!field) return;
        if (values[key]) {
          hasData = true;
          if (!field.value) field.value = values[key];
        }
      });

      if (hasData && !this.personalizeToggle.checked) {
        this.personalizeToggle.checked = true;
        this._onPersonalizeToggle();
      }

      this._onNoteInput();
    }

    // The mini cart already traps focus, a nested trap would fight with it.
    get shouldTrapFocus() {
      return false;
    }

    _onCartDrawerClose(event) {
      if (event.target !== this.cartDrawer) return;
      this.open = false;
    }

    _onClick(event) {
      if (this.isSaving) return;

      if (event.target.closest('[data-customizer-gift-wrap-toggle]')) {
        this.pendingGiftWrap = !this.pendingGiftWrap;
        this._renderGiftWrap();
        return;
      }

      const addButton = event.target.closest('[data-customizer-product-add]');
      if (addButton) {
        const card = addButton.closest('[data-customizer-product]');
        if (card?.dataset.variantId) this._setQuantity(card.dataset.variantId, 1);
        return;
      }

      const step = event.target.closest('[data-customizer-quantity-step]');
      if (step) {
        const card = step.closest('[data-customizer-product]');
        const variantId = card?.dataset.variantId;
        if (!variantId) return;

        const current = this.pendingQuantities[variantId] || 0;
        this._setQuantity(variantId, current + parseInt(step.dataset.customizerQuantityStep, 10));
        return;
      }

      if (event.target.closest('[data-customizer-save]')) {
        this._save();
      }
    }

    _setQuantity(variantId, quantity) {
      this.pendingQuantities[variantId] = Math.min(Math.max(quantity, 0), 99);
      this._renderProduct(variantId);
    }

    _renderGiftWrap() {
      if (!this.giftWrapToggle) return;

      const { addLabel, addedLabel } = this.giftWrapToggle.dataset;

      this.giftWrapToggle.textContent = this.pendingGiftWrap ? addedLabel : addLabel;
      this.giftWrapToggle.classList.toggle('is-selected', this.pendingGiftWrap);
      this.giftWrapToggle.setAttribute('aria-pressed', String(this.pendingGiftWrap));
    }

    _renderProduct(variantId) {
      const card = this.productElements.find((el) => el.dataset.variantId === String(variantId));
      if (!card) return;

      const quantity = this.pendingQuantities[variantId] || 0;
      const addButton = card.querySelector('[data-customizer-product-add]');
      const quantitySelector = card.querySelector('[data-customizer-quantity]');
      const quantityValue = card.querySelector('[data-customizer-quantity-value]');
      const isSelected = quantity > 0;

      if (addButton) addButton.hidden = isSelected;
      if (quantitySelector) quantitySelector.hidden = !isSelected;
      if (quantityValue) quantityValue.textContent = String(Math.max(quantity, 1));
      card.classList.toggle('is-selected', isSelected);
    }

    _setSavingState(isSaving) {
      this.isSaving = isSaving;
      this.classList.toggle('is-saving', isSaving);

      if (this.saveButton) {
        this.saveButton.disabled = isSaving;
        this.saveButton.setAttribute('aria-busy', String(isSaving));
      }
    }

    async _save() {
      if (this.isSaving) return;

      if (!this._validatePersonalization()) return;

      this._setSavingState(true);

      try {
        let cart = await this._fetchCart();

        cart = await this._applyProduct(cart);

        cart = await this._applyPersonalization(cart);

        const giftWrap = await this._applyGiftWrap(cart);

        cart = giftWrap.cart;

        this.open = false;

        if (!giftWrap.refreshed) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart, item_count: cart.item_count } })
          );

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { replacementDelay: 0 } })
          );
        }
      } catch (error) {
        console.error('[order-customizer]', error);

        document.documentElement.dispatchEvent(
          new CustomEvent('cart-notification:show', {
            bubbles: true,
            detail: { status: 'error', error: error.message || 'Unable to update your cart. Please try again.' }
          })
        );
      } finally {
        this._setSavingState(false);
      }
    }

    /**
     * Brings each add-on variant line to its selected quantity (adding, updating or
     * removing it) and returns the resulting cart.
     */
    async _applyProduct(cart) {
      const variantIds = Object.keys(this.pendingQuantities);
      if (variantIds.length === 0) return cart;

      const updates = {};
      let needsUpdate = false;

      variantIds.forEach((variantId) => {
        const pendingQuantity = this.pendingQuantities[variantId] || 0;
        const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
        const currentQuantity = line ? line.quantity : 0;

        if (currentQuantity !== pendingQuantity) {
          updates[variantId] = pendingQuantity;
          needsUpdate = true;
        }
      });

      if (!needsUpdate) return cart;

      // update.js takes an absolute quantity per variant, so it covers add, change and remove.
      return this._postCart(`${window.themeVariables.routes.cartUrl}/update.js`, { updates });
    }

    /**
     * Saves the gift personalization as the cart note (Gift Message) and cart
     * attributes (which carry through to the order as note attributes). When the
     * personalization checkbox is unchecked, the fields are cleared. This runs
     * against /cart/update.js and does not touch the checkout flow itself.
     */
    async _applyPersonalization(cart) {
      if (!this.personalizeToggle) return cart;

      const enabled = this.personalizeToggle.checked;
      const readField = (key) => {
        const field = this.personalizeFields[key];
        return enabled && field ? field.value.trim() : '';
      };

      const message = readField('note');
      const recipientName = readField('recipient_name');
      const recipientPhone = readField('recipient_phone');
      const senderName = readField('sender_name');

      // When disabled, only write (to clear) if this feature had previously saved data,
      // so we never wipe a note/attributes that were set elsewhere.
      if (!enabled) {
        const attrs = cart.attributes || {};
        const hadData = Boolean(
          attrs['Gift Message'] ||
            attrs["Recipient's Name"] ||
            attrs["Recipient's Phone Number"] ||
            attrs["Sender's Name"]
        );

        if (!hadData) return cart;
      }

      return this._postCart(`${window.themeVariables.routes.cartUrl}/update.js`, {
        note: message,
        attributes: {
          'Gift Message': message,
          "Recipient's Name": recipientName,
          "Recipient's Phone Number": recipientPhone,
          "Sender's Name": senderName
        }
      });
    }

    /**
     * Delegates to the theme's <gift-wrap> element so the gift wrap quantity keeps
     * mirroring the number of items in the cart.
     *
     * Returns the resulting cart plus whether <gift-wrap> already re-rendered the mini cart.
     */
    async _applyGiftWrap(cart) {
      if (!this.giftWrapElement || !this.giftWrapHandle) return { cart, refreshed: false };

      const line = cart.items.find((item) => item.handle === this.giftWrapHandle);

      if (this.giftWrapCheckbox) this.giftWrapCheckbox.checked = this.pendingGiftWrap;

      if (this.pendingGiftWrap) {
        const expectedQuantity = cart.items
          .filter((item) => item.handle !== this.giftWrapHandle)
          .reduce((total, item) => total + (item.quantity || 0), 0);

        if (expectedQuantity === 0 || (line && line.quantity === expectedQuantity)) {
          return { cart, refreshed: false };
        }

        if (typeof this.giftWrapElement._addGiftWrap === 'function') {
          await this.giftWrapElement._addGiftWrap();
          return { cart, refreshed: true };
        }

        return {
          cart: await this._updateGiftWrapQuantity(expectedQuantity),
          refreshed: false
        };
      }

      if (!line) return { cart, refreshed: false };

      if (typeof this.giftWrapElement._removeGiftWrap === 'function') {
        await this.giftWrapElement._removeGiftWrap();
        return { cart, refreshed: true };
      }

      return { cart: await this._updateGiftWrapQuantity(0), refreshed: false };
    }

    /**
     * Fallback used only when the theme's <gift-wrap> element is unavailable.
     */
    async _updateGiftWrapQuantity(quantity) {
      const response = await fetch(`/products/${this.giftWrapHandle}.js`);

      if (!response.ok) throw new Error('Gift wrap product not found');

      const product = await response.json();

      return this._postCart(`${window.themeVariables.routes.cartUrl}/update.js`, {
        updates: { [product.variants[0].id]: quantity }
      });
    }

    async _fetchCart() {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, { cache: 'no-store' });

      if (!response.ok) throw new Error('Unable to read the cart');

      return response.json();
    }

    async _postCart(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to update the cart');
      }

      return response.json();
    }
  }

  if (!customElements.get('order-customizer')) {
    customElements.define('order-customizer', OrderCustomizer);
  }

  /**
   * When the cart is emptied, wipe gift personalization (note + attributes) so the
   * next order does not inherit the previous message. Runs at document level because
   * <order-customizer> is removed from the DOM on an empty cart re-render.
   */
  {
    const PERSONALIZE_ATTRIBUTE_KEYS = [
      'Gift Message',
      "Recipient's Name",
      "Recipient's Phone Number",
      "Sender's Name"
    ];

    let clearingPersonalization = false;

    const cartHasPersonalization = (cart) => {
      if (!cart) return false;
      if (cart.note) return true;
      const attrs = cart.attributes || {};
      return PERSONALIZE_ATTRIBUTE_KEYS.some((key) => Boolean(attrs[key]));
    };

    document.documentElement.addEventListener('cart:updated', async (event) => {
      const cart = event?.detail?.cart;
      if (!cart || cart.item_count > 0 || clearingPersonalization) return;
      if (!cartHasPersonalization(cart)) return;

      clearingPersonalization = true;

      try {
        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';
        const attributes = {};
        PERSONALIZE_ATTRIBUTE_KEYS.forEach((key) => {
          attributes[key] = '';
        });

        await fetch(`${cartUrl}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: '', attributes })
        });
      } catch (error) {
        console.error('[order-customizer] failed to clear personalization on empty cart', error);
      } finally {
        clearingPersonalization = false;
      }
    });
  }
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * RAKHI ADD-ON CARD
 * ------------------------------------------------------------------------------------------------------------
 *
 * Color swatches follow the product-item pattern (radio + data-variant-* attrs).
 * Selecting a swatch updates the hidden `.charm-checkbox` variant id, price, and image.
 * The "Add" button toggles that checkbox so the product-form submit handler can pick it up.
 */
{
  class RakhiAddon extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-rakhi-checkbox], .rakhi-addon__checkbox');
      this.addButton = this.querySelector('.rakhi-addon__add');
      this.addLabel = this.querySelector('.rakhi-addon__add-label');
      this.priceEl = this.querySelector('[data-rakhi-price]');
      this.compareEl = this.querySelector('[data-rakhi-compare]');
      this.imageEl = this.querySelector('[data-rakhi-image], .rakhi-addon__image');
      this.drawerId = this.getAttribute('data-drawer-id');

      if (!this.checkbox || !this.addButton) return;

      this._onAddClick = this._toggleAdd.bind(this);
      this._onDrawerAddClick = this._onDrawerAdd.bind(this);
      this._onSwatchChange = this._onSwatch.bind(this);
      this._onChange = this._syncState.bind(this);

      this.addButton.addEventListener('click', this._onAddClick);
      this.checkbox.addEventListener('change', this._onChange);

      this._bindDrawer();
      // append-body may relocate the popover after first paint
      requestAnimationFrame(() => this._bindDrawer());

      this._syncState();
    }

    disconnectedCallback() {
      this.addButton?.removeEventListener('click', this._onAddClick);
      this.drawerAdd?.removeEventListener('click', this._onDrawerAddClick);
      this.checkbox?.removeEventListener('change', this._onChange);
      this.swatchInputs?.forEach((input) => input.removeEventListener('change', this._onSwatchChange));
      const drawer = this._getDrawer();
      if (drawer && this._onDrawerOpen) {
        drawer.removeEventListener('openable-element:open', this._onDrawerOpen);
        drawer.removeEventListener('openable-element:close', this._onDrawerClose);
      }
      document.documentElement.classList.remove('lock-all');
    }

    _getDrawer() {
      if (this.drawer?.isConnected) return this.drawer;
      this.drawer =
        (this.drawerId && document.getElementById(this.drawerId)) ||
        this.querySelector('.rakhi-addon__drawer');
      return this.drawer;
    }

    _bindDrawer() {
      const drawer = this._getDrawer();
      if (!drawer) return;

      this.drawerAdd = drawer.querySelector('[data-rakhi-drawer-add], .rakhi-addon__drawer-add');
      this.drawerAddLabel = drawer.querySelector('.rakhi-addon__drawer-add-label');
      this.drawerCarousel = drawer.querySelector('[data-rakhi-drawer-carousel]');
      this.drawerFallbackImage = drawer.querySelector('[data-rakhi-drawer-image]');
      this.mediaTrigger = this.querySelector('.rakhi-addon__media');

      this.swatchInputs?.forEach((input) => input.removeEventListener('change', this._onSwatchChange));
      this.swatchInputs = Array.from(
        new Set([
          ...this.querySelectorAll('.color-swatch__radio'),
          ...drawer.querySelectorAll('.color-swatch__radio'),
        ])
      );
      this.swatchInputs.forEach((input) => input.addEventListener('change', this._onSwatchChange));

      this.drawerAdd?.removeEventListener('click', this._onDrawerAddClick);
      this.drawerAdd?.addEventListener('click', this._onDrawerAddClick);

      if (!this._onDrawerOpen) {
        this._onDrawerOpen = () => {
          this._preloadDrawerImages();
          document.documentElement.classList.add('lock-all');
        };
        this._onDrawerClose = () => {
          document.documentElement.classList.remove('lock-all');
        };
      }
      drawer.removeEventListener('openable-element:open', this._onDrawerOpen);
      drawer.removeEventListener('openable-element:close', this._onDrawerClose);
      drawer.addEventListener('openable-element:open', this._onDrawerOpen);
      drawer.addEventListener('openable-element:close', this._onDrawerClose);

      // Warm images before open so the modal feels instant
      if (this.mediaTrigger && !this._preloadBound) {
        this._preloadBound = true;
        const warm = () => this._preloadDrawerImages();
        this.mediaTrigger.addEventListener('pointerenter', warm, { once: true });
        this.mediaTrigger.addEventListener('pointerdown', warm, { once: true });
      }
    }

    _preloadDrawerImages() {
      const drawer = this._getDrawer();
      if (!drawer) return;

      const images = drawer.querySelectorAll(
        '.rakhi-addon__drawer-media-item:not(.is-filtered) img, [data-rakhi-drawer-image]'
      );

      images.forEach((img, index) => {
        img.loading = 'eager';
        img.decoding = 'async';
        if (index === 0) img.fetchPriority = 'high';

        const src = img.currentSrc || img.getAttribute('src');
        if (!src || img.complete) return;

        const preloader = new Image();
        if (img.srcset) preloader.srcset = img.srcset;
        preloader.sizes = img.sizes || '440px';
        preloader.src = src;
      });
    }

    _toggleAdd(event) {
      event.preventDefault();
      this.checkbox.checked = !this.checkbox.checked;
      this.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _onDrawerAdd(event) {
      event.preventDefault();
      if (!this.checkbox.checked) {
        this.checkbox.checked = true;
        this.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this._closeDrawer();
    }

    _closeDrawer() {
      const drawer = this._getDrawer();
      if (!drawer) return;
      drawer.open = false;
    }

    _onSwatch(event) {
      const input = event.currentTarget;
      const variantId = input.getAttribute('data-variant-id');
      if (!variantId || input.getAttribute('data-variant-available') === 'false') return;

      this.checkbox.value = variantId;
      this._syncSwatchRadios(input.value);

      // New color = new selection; clear Add so the shopper re-confirms
      if (this.checkbox.checked) {
        this.checkbox.checked = false;
        this.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.priceEl && input.hasAttribute('data-variant-price')) {
        this.priceEl.textContent = this._plainMoney(input.getAttribute('data-variant-price'));
      }

      if (this.compareEl) {
        if (input.hasAttribute('data-variant-compare-price')) {
          this.compareEl.textContent = this._plainMoney(input.getAttribute('data-variant-compare-price'));
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      const imageUrl = input.getAttribute('data-variant-image');
      if (this.imageEl && imageUrl) {
        this.imageEl.src = imageUrl;
        this.imageEl.removeAttribute('srcset');
      }

      this._updateDrawerMedia(input);
      this._preloadDrawerImages();
    }

    _syncSwatchRadios(value) {
      this.swatchInputs?.forEach((input) => {
        input.checked = input.value === value;
      });
    }

    async _updateDrawerMedia(input) {
      const mediaId = input.getAttribute('data-variant-media-id');
      const imageUrl = input.getAttribute('data-variant-image');
      const variantTitle = input.getAttribute('data-variant-title') || '';
      const drawer = this._getDrawer();
      if (!drawer) return;

      this.drawerCarousel = drawer.querySelector('[data-rakhi-drawer-carousel]');
      this.drawerFallbackImage = drawer.querySelector('[data-rakhi-drawer-image]');

      if (this.drawerCarousel) {
        // Restore original media order before filtering (Flickity can shuffle DOM nodes)
        const mediaItems = Array.from(
          this.drawerCarousel.querySelectorAll('.rakhi-addon__drawer-media-item')
        ).sort((a, b) => {
          return (
            parseInt(a.getAttribute('data-original-position'), 10) -
            parseInt(b.getAttribute('data-original-position'), 10)
          );
        });

        mediaItems.forEach((item) => this.drawerCarousel.appendChild(item));

        let firstVisibleId = null;

        mediaItems.forEach((item) => {
          const alt = (item.getAttribute('data-media-alt') || '').trim();
          // Same rule as product-media.liquid:
          // show when variant.title contains media.alt, or media.alt == 'all' (blank also shows)
          const matches =
            alt === 'all' ||
            alt === '' ||
            (variantTitle && variantTitle.indexOf(alt) !== -1);

          item.classList.toggle('is-filtered', !matches);
          item.classList.remove('is-selected', 'is-initial-selected');

          if (matches && firstVisibleId === null) {
            firstVisibleId = item.getAttribute('data-media-id');
          }
        });

        const selectedId =
          mediaId &&
          mediaItems.some(
            (item) =>
              item.getAttribute('data-media-id') === mediaId &&
              !item.classList.contains('is-filtered')
          )
            ? mediaId
            : firstVisibleId;

        if (selectedId) {
          const selectedItem = mediaItems.find(
            (item) => item.getAttribute('data-media-id') === selectedId
          );
          selectedItem?.classList.add('is-selected', 'is-initial-selected');
        }

        try {
          if (typeof this.drawerCarousel.reload === 'function') {
            await this.drawerCarousel.reload();
          }
          // Select by index 0 of visible cells when featured media isn't the first match,
          // otherwise jump to the featured media slide.
          if (selectedId && typeof this.drawerCarousel.select === 'function') {
            await this.drawerCarousel.select(`[data-media-id="${selectedId}"]`);
          }
          return;
        } catch (e) {
          // Fall through to image swap
        }
      }

      if (this.drawerFallbackImage && imageUrl) {
        this.drawerFallbackImage.src = imageUrl;
        this.drawerFallbackImage.removeAttribute('srcset');
      }
    }

    _plainMoney(value) {
      if (!value) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = value;
      return (tmp.textContent || tmp.innerText || '').trim();
    }

    _syncState() {
      const selected = this.checkbox.checked;
      this.classList.toggle('is-selected', selected);
      this.addButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (this.addLabel) {
        this.addLabel.textContent = selected ? 'Added' : 'Add';
      }
      if (this.drawerAddLabel) {
        this.drawerAddLabel.textContent = selected ? 'Added' : 'Add Rakhi';
      }
    }
  }

  if (!customElements.get('rakhi-addon')) {
    customElements.define('rakhi-addon', RakhiAddon);
  }
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * MINI-CART UPSELL TABS (Add-ons / You may also like)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Delegated click handler so it keeps working after the cart drawer re-renders its section HTML.
 * Toggling only switches which panel/tab is active; the "Add" buttons keep using the theme's existing
 * `.mini-cart__upsell-add` handler (theme.js) to add products to the cart.
 */
document.addEventListener('click', function (event) {
  const tab = event.target.closest('.mini-cart__tab');
  if (!tab) return;

  const container = tab.closest('[data-cart-tabs]');
  if (!container) return;

  const target = tab.getAttribute('data-tab');

  container.querySelectorAll('.mini-cart__tab').forEach((button) => {
    const isActive = button === tab;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  container.querySelectorAll('.mini-cart__tab-panel').forEach((panel) => {
    panel.classList.toggle('is-active', panel.getAttribute('data-panel') === target);
  });
});


class SanTooltip extends HTMLElement {
  constructor() {
    super();
    this._activeHotspot = null;
    this._isTouch = window.matchMedia('(hover: none)').matches;
  }

  connectedCallback() {
    this._hotspots = Array.from(this.querySelectorAll('[data-san-tooltip-hotspot]'));
    if (!this._hotspots.length) return;

    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    this._hotspots.forEach((hotspot) => {
      const card = this.querySelector('#' + hotspot.dataset.target);
      if (!card) return;

      hotspot._card = card;

      if (this._isTouch) {
        hotspot.addEventListener('click', (event) => {
          event.preventDefault();
          this._toggle(hotspot, card);
        });
      } else {
        hotspot.addEventListener('mouseenter', () => this._show(hotspot, card));
        hotspot.addEventListener('mouseleave', () => this._hide(hotspot, card));
        hotspot.addEventListener('focus', () => this._show(hotspot, card));
        hotspot.addEventListener('blur', () => this._hide(hotspot, card));
        card.addEventListener('mouseenter', () => this._show(hotspot, card));
        card.addEventListener('mouseleave', () => this._hide(hotspot, card));
      }

      const closeBtn = card.querySelector('[data-san-tooltip-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this._hide(hotspot, card);
        });
      }
    });

    if (this._isTouch) {
      document.addEventListener('click', this._onDocumentClick);
    }
    window.addEventListener('resize', this._onResize);
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('keydown', this._onKeydown);
  }

  _show(hotspot, card) {
    this._adjustPlacement(hotspot, card);
    hotspot.classList.add('is-active');
    hotspot.setAttribute('aria-expanded', 'true');
    card.classList.add('is-visible');
    this._activeHotspot = hotspot;
  }

  _hide(hotspot, card) {
    hotspot.classList.remove('is-active');
    hotspot.setAttribute('aria-expanded', 'false');
    card.classList.remove('is-visible');
    if (this._activeHotspot === hotspot) this._activeHotspot = null;
  }

  _toggle(hotspot, card) {
    const isOpen = card.classList.contains('is-visible');
    this._hideAll();
    if (!isOpen) this._show(hotspot, card);
  }

  _hideAll() {
    this._hotspots.forEach((hotspot) => {
      if (hotspot._card) this._hide(hotspot, hotspot._card);
    });
  }

  _adjustPlacement(hotspot, card) {
    const panel = hotspot.closest('[data-san-tooltip-panel]');
    if (!panel) return;
    const panelRect = panel.getBoundingClientRect();
    const hotspotRect = hotspot.getBoundingClientRect();
    const cardHeight = card.offsetHeight || 110;
    const spaceAbove = hotspotRect.top - panelRect.top;
    card.classList.toggle('is-flipped', spaceAbove < cardHeight + 24);
  }

  _onDocumentClick(event) {
    if (!this.contains(event.target) && this._activeHotspot) {
      this._hideAll();
    }
  }

  _onResize() {
    this._hideAll();
  }

  _onKeydown(event) {
    if (event.key === 'Escape' && this._activeHotspot) {
      this._hideAll();
    }
  }
}

customElements.define('san-tooltip', SanTooltip);